/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import AdobeExperienceCloud from 'url:@exc/brand-icons/hero/AdobeExperienceCloud.svg';
import AdobeExperiencePlatform from 'url:@exc/brand-icons/hero/AdobeExperiencePlatform.svg';
import AdobeExperienceSubCloud from 'url:@exc/brand-icons/hero/AdobeExperienceSubCloud.svg';
import {getPathWithoutTenant, hashToPath} from '@exc/url';
import HeroIcons from 'url:@exc/brand-icons/HeroIcons.svg';

/**
 * This is something of a stop gap until work hub takes over more configuration work.
 * There exist apps that either have internal integrations, like Analytics,
 * or composed apps that require the same branding to be used for anything opened
 * inside of their context. To avoid needing to add explicit overrides for every
 * edge case or copying multiple things between solution configs and app assembly
 * we're just putting the branding for those cases here for now.
 */
export const COMPOSED_APP_CONFIGS = [
  // AJO is a composed application
  {
    heroConfig: {
      favicon: AdobeExperienceCloud,
      icon: `${HeroIcons}#AdobeExperienceCloud`,
      path: '/journey-optimizer/home',
      shortTitle: 'Journey Optimizer',
      title: 'Adobe Journey Optimizer'
    },
    id: 'journey-optimizer',
    pathMatchFragment: ['/cjm', '/journey-optimizer']
  },
  // Data Collection is a composed application
  {
    heroConfig: {
      favicon: AdobeExperienceCloud,
      icon: `${HeroIcons}#AdobeExperienceCloud`,
      path: '/data-collection',
      shortTitle: 'Data Collection',
      title: 'Adobe Experience Platform Data Collection'
    },
    id: 'data-collection',
    pathMatchFragment: '/data-collection'
  },
  // Primetime has several parts with different appids that need the same branding
  {
    heroConfig: {
      favicon: AdobeExperienceSubCloud,
      icon: `${HeroIcons}#AdobeExperienceSubCloud`,
      path: '/primetime/home',
      shortTitle: 'Primetime',
      title: 'Adobe Primetime'
    },
    id: 'primetime',
    pathMatchFragment: '/primetime/home'
  },
  {
    heroConfig: {
      favicon: AdobeExperienceSubCloud,
      icon: `${HeroIcons}#AdobeExperienceSubCloud`,
      path: '/primetime/aiq',
      shortTitle: 'Primetime Account IQ',
      title: 'Adobe Primetime Account IQ'
    },
    id: 'primetime',
    pathMatchFragment: '/primetime/aiq'
  },
  {
    heroConfig: {
      favicon: AdobeExperienceSubCloud,
      icon: `${HeroIcons}#AdobeExperienceSubCloud`,
      path: '/primetime/dai',
      shortTitle: 'Adobe Primetime Ad Insertion',
      title: 'Adobe Primetime Ad Insertion'
    },
    id: 'primetime',
    pathMatchFragment: '/primetime/dai'
  },
  // Analytics isn't technically a composed app, but does have some internal integrations
  // like data governance that need Analytics branding applied to them
  {
    heroConfig: {
      favicon: AdobeExperienceSubCloud,
      icon: `${HeroIcons}#AdobeExperienceSubCloud`,
      path: '/analytics/spa/index.html',
      shortTitle: 'Analytics',
      title: 'Adobe Analytics'
    },
    id: 'analytics',
    pathMatchFragment: '/analytics'
  }
];

/**
 * These serve as the default branding of apps based off of their app assembly type.
 * These assumptions do not hold true for composed apps or some one off integrations.
 */
export const HERO_CONFIGS = {
  application: {
    favicon: AdobeExperienceSubCloud,
    icon: `${HeroIcons}#AdobeExperienceSubCloud`,
    shortTitle: 'Adobe Experience Cloud'
  },
  cloud: {
    favicon: AdobeExperienceCloud,
    icon: `${HeroIcons}#AdobeExperienceCloud`,
    path: '/home',
    shortTitle: 'Adobe Experience Cloud'
  },
  platform: {
    favicon: AdobeExperiencePlatform,
    icon: `${HeroIcons}#AdobeExperiencePlatform`,
    shortTitle: 'Adobe Experience Platform'
  },
  service: {
    favicon: AdobeExperienceCloud,
    icon: `${HeroIcons}#AdobeExperienceCloud`,
    shortTitle: 'Adobe Experience Cloud'
  }
};

export const getHeroConfig = (shellResponse, solutionConfig, stateHero) => {
  // Grab the current solution or service in order to get the proper name string
  const allSolutionsAndServices = (shellResponse.solutions || []).concat(shellResponse.services || []);
  const hashPath = getPathWithoutTenant(hashToPath());

  const composedAppConfig = COMPOSED_APP_CONFIGS.find(config => {
    let pathstoMatch = [].concat(config.pathMatchFragment);
    return pathstoMatch.find(path => hashPath.startsWith(path));
  });

  const current = allSolutionsAndServices.find(solServ => {
    const apps = Array.isArray(solServ.appId) ? solServ.appId : [solServ.appId];
    const appId = hashPath.includes('/journey-optimizer/') ? 'cjm-home' : solutionConfig.appId;
    return apps.indexOf(appId) > -1;
  });

  let newHero = HERO_CONFIGS.cloud;
  const type = (current && current.adobeBrandType) || 'cloud';
  let {longName, name} = current || {};

  if (type !== 'cloud' || longName === 'Admin Tool') {
    // State hero is used here to push runtime changes forward in case getHeroConfig
    // is called in such a way that App Assembly load or a Solution config change occurs
    // after a meaningful runtime change has been made.
    let runtimeOverrides = {};
    if (stateHero) {
      runtimeOverrides = {path: stateHero.path};
      if (solutionConfig?.sandbox?.thirdParty) {
        runtimeOverrides = stateHero;
      }
    }

    const baseHeroConfig = HERO_CONFIGS[type];
    // Precedence: Runtime set hero values, solution set hero values, default type values.
    newHero = Object.assign({}, baseHeroConfig, {
      // shortTitle / name is the only required label for a solution, so all other labels fallback to it
      // or are generated from it.
      shortTitle: name || baseHeroConfig.shortTitle,
      title: longName || name || baseHeroConfig.title
    }, runtimeOverrides);
  }

  // Composed apps should override everything else (currently) with shared branding / behavior
  if (composedAppConfig) {
    newHero = Object.assign({composed: true}, newHero, composedAppConfig.heroConfig);
  }

  // Find and set the favicon to the new hero icon
  document.querySelectorAll('#favicon').forEach(link => {
    link.href = newHero.favicon;
  });

  return newHero;
};
